import {
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiLock } from "react-icons/fi";
import { InternalMessageDto, SourcingEventV2Dto, useAddInternalMessageToSeV2Mutation } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { formatDateTime, personName } from "../../../common/components/communication/communicationUtils";

const InternalMessageItem = ({ message }: { message: InternalMessageDto }) => (
  <Box borderWidth="1px" borderColor="smBorder" rounded="md" px="3" py="2" bg="smBackground">
    <Text fontSize="xs" color="smMuted" mb="1">
      {message.createdBy ? personName(message.createdBy) : "—"} · {formatDateTime(message.createdAt)}
    </Text>
    <Text fontSize="sm" whiteSpace="pre-wrap">
      {message.value}
    </Text>
  </Box>
);

export const InternalNotesSection = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [addMessage, { isLoading }] = useAddInternalMessageToSeV2Mutation();
  const displayer = useApiError();

  const messages = event.internalConversation ?? [];

  const handleSubmit = async () => {
    const value = draft.trim();
    if (!value) return;
    try {
      await addMessage({
        eventId: event.id,
        addInternalMessageRequest: { value },
      }).unwrap();
      setDraft("");
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" rounded="lg" mt="2" bg="smBackground">
      <Stack px="4" py="4" spacing="3">
        <HStack spacing="2">
          <Icon as={FiLock} boxSize="4" color="smMuted" />
          <Text fontSize="sm" color="smMuted">
            {t("Only visible to your organization.")}
          </Text>
        </HStack>
        {messages.length === 0 && (
          <Text fontSize="sm" color="smMuted">
            {t("No internal messages yet.")}
          </Text>
        )}
        {messages.map((msg) => (
          <InternalMessageItem key={msg.id} message={msg} />
        ))}

        <HStack spacing="2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("Add an internal message...") ?? undefined}
            size="sm"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <Button
            size="sm"
            colorScheme="teal"
            isLoading={isLoading}
            isDisabled={!draft.trim()}
            onClick={handleSubmit}
          >
            {t("Add")}
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
};
